// src/hooks/useExamRegistrations.js
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

/** Cache key */
export const examRegistrationsKey = (studentId) => ['exam_registrations', String(studentId || 'unknown')];

/** Utilities */
const bySubjectCodeNumeric = (a, b) => {
  const ac = (a?.subjects?.code || '').trim();
  const bc = (b?.subjects?.code || '').trim();
  const anum = parseInt(ac.replace(/\D+/g, ''), 10);
  const bnum = parseInt(bc.replace(/\D+/g, ''), 10);
  if (!Number.isNaN(anum) && !Number.isNaN(bnum) && anum !== bnum) return anum - bnum;
  return ac.localeCompare(bc, 'en', { sensitivity: 'base', numeric: true });
};

/** READ — sessions + embedded children (filtered to is_deleted = false) */
async function fetchExamRegistrations({ studentId }) {
  if (!studentId) return [];

  // Fetch parent with joins; filter active parent and active children
  const { data, error } = await supabase
    .from('student_exam_session')
    .select(`
      id,
      student_id,
      exam_session_id,
      registration_status,
      venue,
      total_fee_paid,
      payment_date,
      remarks,
      is_deleted,
      created_at,
      updated_at,
      exam_sessions:exam_sessions ( id, name, start_date, end_date ),
      infos:student_exam_info (
        id,
        session_registration_id,
        student_id,
        subject_id,
        attempt_no,
        registration_status,
        result_status,
        notes,
        is_deleted,
        subjects:subjects ( id, code, name )
      )
    `)
    .eq('student_id', studentId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Keep only active children and sort by subject code the same way as Admin pages
  return (data || []).map((sess) => ({
    ...sess,
    infos: (sess.infos || [])
      .filter((r) => r && r.is_deleted === false)
      .slice()
      .sort(bySubjectCodeNumeric),
  }));
}

/** CREATE — upsert parent (if needed) + insert child rows for selected subjects */
async function createExamRegistration({
  studentId,
  exam_session_id,
  subject_ids = [],
  parent_registration_status = 'Registered', // matches Admin
  venue = null,
  total_fee_paid = null,
  payment_date = null,
  remarks = null,
}) {
  if (!studentId) throw new Error('studentId is required');
  if (!exam_session_id) throw new Error('exam_session_id is required');
  if (!Array.isArray(subject_ids) || subject_ids.length === 0) throw new Error('subject_ids required');

  // 1) Find existing parent for (student, session)
  const { data: found, error: fErr } = await supabase
    .from('student_exam_session')
    .select('id')
    .eq('student_id', studentId)
    .eq('exam_session_id', exam_session_id)
    .eq('is_deleted', false)
    .limit(1);
  if (fErr) throw fErr;

  const parentPayload = {
    student_id: studentId,
    exam_session_id,
    registration_status: parent_registration_status,
    venue: venue || null,
    total_fee_paid: total_fee_paid == null || total_fee_paid === '' ? null : Number(total_fee_paid),
    payment_date: payment_date || null,
    remarks: remarks || null,
    is_deleted: false,
  };

  let parentId;
  if (found && found.length > 0) {
    parentId = found[0].id;
    const { error: uErr } = await supabase
      .from('student_exam_session')
      .update(parentPayload)
      .eq('id', parentId);
    if (uErr) throw uErr;
  } else {
    const { data: created, error: cErr } = await supabase
      .from('student_exam_session')
      .insert(parentPayload)
      .select('id')
      .single();
    if (cErr) throw cErr;
    parentId = created.id;
  }

  // 2) Compute next attempt_no per subject for this student
  const { data: prevAttempts, error: aErr } = await supabase
    .from('student_exam_info')
    .select('subject_id, attempt_no')
    .eq('student_id', studentId)
    .eq('is_deleted', false)
    .in('subject_id', subject_ids);
  if (aErr) throw aErr;

  const maxBySubject = {};
  for (const r of prevAttempts || []) {
    const k = String(r.subject_id);
    maxBySubject[k] = Math.max(Number(maxBySubject[k] || 0), Number(r.attempt_no || 0));
  }

  const childRows = subject_ids.map((sid) => ({
    session_registration_id: parentId,
    student_id: studentId,
    subject_id: sid,
    attempt_no: (maxBySubject[String(sid)] || 0) + 1,
    registration_status: 'Registered',
    result_status: 'Pending',
    notes: null,
    is_deleted: false,
  }));

  const { error: insErr } = await supabase.from('student_exam_info').insert(childRows);
  if (insErr) throw insErr;

  return parentId;
}

/** UPDATE — parent session fields */
async function updateExamRegistrationSession({ id, updates }) {
  if (!id) throw new Error('id is required');
  const payload = { ...updates, updated_at: new Date().toISOString() };
  const { error } = await supabase.from('student_exam_session').update(payload).eq('id', id).eq('is_deleted', false);
  if (error) throw error;
}

/** UPDATE — single child info row */
async function updateExamRegistrationInfo({ id, updates }) {
  if (!id) throw new Error('id is required');
  const payload = { ...updates, updated_at: new Date().toISOString() };
  const { error } = await supabase.from('student_exam_info').update(payload).eq('id', id).eq('is_deleted', false);
  if (error) throw error;
}

/** SOFT DELETE — parent + its children */
async function softDeleteExamRegistrationSession({ id }) {
  if (!id) throw new Error('id is required');
  const now = new Date().toISOString();

  const { error: pErr } = await supabase
    .from('student_exam_session')
    .update({ is_deleted: true, updated_at: now })
    .eq('id', id);
  if (pErr) throw pErr;

  const { error: cErr } = await supabase
    .from('student_exam_info')
    .update({ is_deleted: true, updated_at: now })
    .eq('session_registration_id', id);
  if (cErr) throw cErr;
}

/** RESTORE — parent + its children */
async function restoreExamRegistrationSession({ id }) {
  if (!id) throw new Error('id is required');
  const now = new Date().toISOString();

  const { error: pErr } = await supabase
    .from('student_exam_session')
    .update({ is_deleted: false, updated_at: now })
    .eq('id', id);
  if (pErr) throw pErr;

  const { error: cErr } = await supabase
    .from('student_exam_info')
    .update({ is_deleted: false, updated_at: now })
    .eq('session_registration_id', id);
  if (cErr) throw cErr;
}

/** Hook */
export function useExamRegistrations(studentId) {
  const qc = useQueryClient();

  const { data = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: examRegistrationsKey(studentId),
    queryFn: () => fetchExamRegistrations({ studentId }),
    enabled: !!studentId,
  });

  const createMutation = useMutation({
    mutationFn: createExamRegistration,
    onSuccess: (_id, vars) => qc.invalidateQueries({ queryKey: examRegistrationsKey(vars.studentId) }),
  });

  const updateSessionMutation = useMutation({
    mutationFn: updateExamRegistrationSession,
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: examRegistrationsKey(vars.studentId) }),
  });

  const updateInfoMutation = useMutation({
    mutationFn: updateExamRegistrationInfo,
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: examRegistrationsKey(vars.studentId) }),
  });

  const softDeleteMutation = useMutation({
    mutationFn: softDeleteExamRegistrationSession,
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: examRegistrationsKey(vars.studentId) }),
  });

  const restoreMutation = useMutation({
    mutationFn: restoreExamRegistrationSession,
    onSuccess: (_r, vars) => qc.invalidateQueries({ queryKey: examRegistrationsKey(vars.studentId) }),
  });

  return useMemo(
    () => ({
      data,
      isLoading,
      isError,
      error,
      refetch,
      // Mutations
      createExamRegistration: createMutation.mutateAsync,
      updateExamRegistrationSession: updateSessionMutation.mutateAsync,
      updateExamRegistrationInfo: updateInfoMutation.mutateAsync,
      softDeleteExamRegistrationSession: softDeleteMutation.mutateAsync,
      restoreExamRegistrationSession: restoreMutation.mutateAsync,
    }),
    [
      data,
      isLoading,
      isError,
      error,
      refetch,
      createMutation.mutateAsync,
      updateSessionMutation.mutateAsync,
      updateInfoMutation.mutateAsync,
      softDeleteMutation.mutateAsync,
      restoreMutation.mutateAsync,
    ]
  );
}
